import { useCallback, useEffect, useRef, useState } from 'react';

// ── Window type declarations ──────────────────────────────────────────────────

declare global {
  interface Window {
    /** reCAPTCHA Enterprise API (loaded via enterprise.js?render=SITE_KEY) */
    grecaptcha?: {
      enterprise: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
        render: (
          container: HTMLElement,
          options: {
            sitekey: string;
            callback: (token: string) => void;
            'expired-callback'?: () => void;
            'error-callback'?: () => void;
          },
        ) => number;
        reset: (widgetId: number) => void;
        getResponse: (widgetId: number) => string;
      };
    };
  }
}

interface UseRecaptchaReturn {
  /** Whether the reCAPTCHA v3 script is loaded and ready */
  isReady: boolean;
  /** Execute reCAPTCHA v3 for a given action, returns token or null on failure */
  execute: (action: string) => Promise<string | null>;
  /** Lazily render v2 checkbox widget into the given container (script already loaded by v3) */
  renderV2Widget: (container: HTMLElement) => Promise<void>;
  /** Register callback for when user solves the v2 puzzle */
  onV2Solved: (callback: (token: string) => void) => void;
  /** Reset the v2 widget (for retry after expiry) */
  resetV2: () => void;
  /** Reset internal state (v3 is stateless, kept for API compatibility) */
  reset: () => void;
}

// ── Shared Promise singleton ────────────────────────────────────────────────
// First caller creates the promise, concurrent callers share it.
// On error the cache is nulled so the next call retries.

let v3LoadPromise: Promise<void> | null = null;

// ── v3 script loader (reCAPTCHA Enterprise) ──────────────────────────────────
// Uses enterprise.js — all API calls go through grecaptcha.enterprise.*
// enterprise.js?render=KEY provides both v3 execute() and v2 render() from a single script.

function loadV3Script(siteKey: string): Promise<void> {
  if (v3LoadPromise) return v3LoadPromise;

  v3LoadPromise = new Promise<void>((resolve, reject) => {
    // Already loaded — just wait for .ready()
    if (window.grecaptcha?.enterprise?.execute) {
      window.grecaptcha.enterprise.ready(() => resolve());
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha?.enterprise?.ready(() => resolve());
    };
    script.onerror = () => {
      v3LoadPromise = null; // allow retry
      reject(new Error('Failed to load reCAPTCHA Enterprise script'));
    };
    document.head.appendChild(script);
  });

  return v3LoadPromise;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Shared reCAPTCHA hook — dual v3 (invisible scoring) + v2 (image challenge fallback).
 *
 * Uses reCAPTCHA Enterprise (enterprise.js) — all API calls go through grecaptcha.enterprise.*
 *
 * Primary flow (v3):
 *   - Loads enterprise.js eagerly on mount via VITE_RECAPTCHA_V3_SITE_KEY
 *   - execute(action) returns invisible token silently
 *
 * Challenge flow (v2):
 *   - When backend returns RECAPTCHA_CHALLENGE_REQUIRED, call renderV2Widget()
 *   - Renders v2 checkbox into provided container (same enterprise.js script, no extra load)
 *   - onV2Solved() fires when user solves the image puzzle
 *   - Uses existing VITE_RECAPTCHA_SITE_KEY (v2 key)
 *
 * Graceful degradation:
 *   - No VITE_RECAPTCHA_V3_SITE_KEY → execute() returns null
 *   - No VITE_RECAPTCHA_SITE_KEY → renderV2Widget() skips silently
 */
const RECAPTCHA_DISABLED = import.meta.env.VITE_RECAPTCHA_DISABLE === 'true';

export function useRecaptcha(): UseRecaptchaReturn {
  const [isReady, setIsReady] = useState(RECAPTCHA_DISABLED);
  const v3SiteKeyRef = useRef(
    RECAPTCHA_DISABLED ? '' : (import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY ?? ''),
  );
  const v2SiteKeyRef = useRef(
    RECAPTCHA_DISABLED ? '' : (import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? ''),
  );
  const v2WidgetIdRef = useRef<number | null>(null);
  const v2CallbackRef = useRef<((token: string) => void) | null>(null);

  // ── v3: load script eagerly ──────────────────────────────────────────────
  useEffect(() => {
    if (RECAPTCHA_DISABLED || !v3SiteKeyRef.current) {
      setIsReady(true);
      return;
    }

    loadV3Script(v3SiteKeyRef.current)
      .then(() => {
        setIsReady(true);
      })
      .catch(() => {
        setIsReady(true);
      });
  }, []);

  // ── v3: execute (invisible scoring) ──────────────────────────────────────
  const execute = useCallback(async (action: string): Promise<string | null> => {
    if (RECAPTCHA_DISABLED) return null;

    const siteKey = v3SiteKeyRef.current;
    if (!siteKey) {
      return null;
    }

    try {
      const token = await new Promise<string>((resolve, reject) => {
        if (!window.grecaptcha?.enterprise?.execute) {
          reject(new Error('grecaptcha.enterprise not available'));
          return;
        }
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha?.enterprise?.execute(siteKey, { action }).then(resolve).catch(reject);
        });
      });
      return token;
    } catch {
      return null;
    }
  }, []);

  // ── v2: render checkbox widget (uses same enterprise.js, no extra script load) ──
  const renderV2Widget = useCallback(async (container: HTMLElement) => {
    if (RECAPTCHA_DISABLED) return;

    const v2SiteKey = v2SiteKeyRef.current;
    if (!v2SiteKey) {
      return;
    }

    try {
      // Ensure enterprise.js is loaded (v3 loads it eagerly, but guard against race)
      if (v3SiteKeyRef.current) {
        await loadV3Script(v3SiteKeyRef.current);
      }

      if (!window.grecaptcha?.enterprise?.render) {
        return;
      }

      v2WidgetIdRef.current = window.grecaptcha.enterprise.render(container, {
        sitekey: v2SiteKey,
        callback: (token: string) => {
          v2CallbackRef.current?.(token);
        },
        'expired-callback': () => {
          if (v2WidgetIdRef.current !== null && window.grecaptcha) {
            window.grecaptcha.enterprise.reset(v2WidgetIdRef.current);
          }
        },
        'error-callback': () => {
          // Widget error — no action needed, user can retry
        },
      });
    } catch {
      // Widget render can fail in edge cases — safe to ignore
    }
  }, []);

  // ── v2: register solved callback ─────────────────────────────────────────
  const onV2Solved = useCallback((callback: (token: string) => void) => {
    v2CallbackRef.current = callback;
  }, []);

  // ── v2: reset widget ────────────────────────────────────────────────────
  const resetV2 = useCallback(() => {
    if (v2WidgetIdRef.current !== null && window.grecaptcha?.enterprise) {
      window.grecaptcha.enterprise.reset(v2WidgetIdRef.current);
    }
  }, []);

  // ── v3: reset (no-op, kept for API compatibility) ────────────────────────
  const reset = useCallback(() => {
    // v3 tokens are single-use and obtained on-demand, so reset is a no-op.
  }, []);

  return { isReady, execute, renderV2Widget, onV2Solved, resetV2, reset };
}
